import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';

export const ServiceSecurite = () => {
  return (
    <div className="min-h-screen pt-24 pb-16 relative overflow-hidden">
      {/* Décors d'ambiance subtils */}
      <div className="absolute top-40 -left-20 w-72 h-72 bg-gradient-to-br from-blue-200/20 to-cyan-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-[700px] -right-20 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-pink-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 left-1/3 w-96 h-96 bg-gradient-to-br from-purple-200/15 to-blue-200/15 rounded-full blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-8">
          <Link to="/services" className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center mb-4">
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
            Retour aux services
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Sécurité & sauvegarde
          </h1>
          <p className="text-xl text-gray-600">
            Je mets l'accent sur la sécurité de vos données : pare-feu, antivirus, filtrage, gestion des droits, sauvegardes locales et cloud.
          </p>
        </div>

        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <p className="text-lg text-gray-800">
              Nous définissons des mécanismes de sauvegarde et de reprise après incident (PRA) adaptés à votre taille d'entreprise, pour que vous puissiez continuer à travailler même en cas de problème.
            </p>
          </CardContent>
        </Card>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Domaines d'intervention</h2>
          <div className="space-y-3">
            {[
              'Mise en place et configuration de pare-feu',
              'Déploiement de solutions antivirus et anti-malware',
              'Gestion des droits d\'accès et des authentifications',
              'Mise en place de politiques de mots de passe robustes',
              'Segmentation réseau pour isoler les usages sensibles',
              'Sauvegardes automatisées locales et cloud',
              'Tests réguliers de restauration',
              'Plan de reprise d\'activité (PRA) adapté',
              'Sensibilisation des utilisateurs aux bonnes pratiques'
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Les bénéfices</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Protection des données</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Vos informations sensibles sont protégées contre les menaces</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Continuité d'activité</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Capacité à reprendre rapidement après un incident</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Sérénité</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Visibilité sur l'état des sauvegardes, tests réguliers</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Conformité</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Respect des bonnes pratiques et exigences réglementaires</p>
              </CardContent>
            </Card>
          </div>
        </div>

        <Card className="mb-8 border-gray-300">
          <CardContent className="pt-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Approche pragmatique</h3>
            <p className="text-gray-600 mb-4">
              La sécurité n'est pas qu'une question d'outils : c'est aussi une question d'organisation et de bonnes pratiques. 
              Je vous aide à mettre en place une stratégie de sécurité réaliste et adaptée à votre contexte.
            </p>
            <p className="text-gray-600">
              Pour les sauvegardes, nous définissons ensemble ce qui doit être sauvegardé, à quelle fréquence, et comment le vérifier régulièrement.
            </p>
          </CardContent>
        </Card>

        <div className="bg-gradient-to-br from-blue-50 to-gray-50 rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Préoccupé par la sécurité de vos données ?</h3>
          <p className="text-gray-600 mb-6">
            Faisons un point sur votre situation et identifions les priorités.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
              <a href="https://outlook.office.com/book/SAADINFORMATIQUE1@saad-informatique.fr/?ismsaljsauthenabled" target="_blank" rel="noopener noreferrer">
                <Phone className="w-5 h-5 mr-2" />
                Prendre RDV
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
              <Link to="/contact">
                <Mail className="w-5 h-5 mr-2" />
                Me contacter
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};